import React from 'react';
import { UI_CONFIG } from '../data/constants';

// SearchBar Component - Extracted from App.js for better maintainability
// Handles search functionality, bookmarks display, and practice mode toggle

const SearchBar = ({ 
  searchTerm, 
  setSearchTerm, 
  showBookmarks, 
  setShowBookmarks, 
  practiceMode, 
  setPracticeMode, 
  bookmarkedConcepts, 
  searchConcepts, 
  getBookmarkedConcepts, 
  setSelectedConcept, 
  toggleBookmark 
}) => (
  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
    <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
      <div className="flex-1">
        <input
          type="text"
          placeholder={UI_CONFIG.DEFAULT_SEARCH_PLACEHOLDER}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          aria-label="Search for security concepts"
          aria-describedby="search-help"
        />
        <div id="search-help" className="sr-only">
          Search through all CompTIA Security+ concepts, explanations, and examples. Results will appear below as you type.
        </div>
      </div>
      <button
        onClick={() => setShowBookmarks(!showBookmarks)}
        className={`px-4 py-2 rounded-lg transition-colors ${
          showBookmarks ? 'bg-yellow-500 text-white' : 'bg-gray-200 text-gray-700'
        }`}
        title={`${showBookmarks ? 'Hide' : 'Show'} bookmarked concepts`}
        aria-label={`${showBookmarks ? 'Hide' : 'Show'} bookmarked concepts`}
        aria-pressed={showBookmarks}
      >
        ★ Bookmarks ({bookmarkedConcepts.size})
      </button>
      <button
        onClick={() => setPracticeMode(!practiceMode)}
        className={`px-4 py-2 rounded-lg transition-colors ${
          practiceMode ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-700'
        }`}
        aria-label={`${practiceMode ? 'Disable' : 'Enable'} practice mode`}
        aria-pressed={practiceMode}
      >
        Practice Mode
      </button>
    </div>
    {searchTerm && searchTerm.length >= 2 && (
      <div className="mt-4">
        <h4 className="font-semibold mb-2">
          🔍 Search Results ({searchConcepts(searchTerm).length})
          {searchTerm.length >= 2 && searchConcepts(searchTerm).length === 0 && (
            <span className="text-sm font-normal text-gray-500 ml-2">- No matches found</span>
          )}
        </h4>
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {searchConcepts(searchTerm).map((concept, index) => (
            <div
              key={concept.key}
              className="p-3 bg-white border rounded cursor-pointer hover:bg-blue-50 transition-colors duration-200 hover:shadow-md"
              onClick={() => setSelectedConcept(concept)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedConcept(concept);
                }
              }}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="font-semibold text-blue-600 mb-1">{concept.title || 'Untitled'}</div>
                  <div className="text-sm text-gray-600 mb-2 line-clamp-2">{concept.explanation || 'No description available'}</div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                      {concept.domain || 'General'}
                    </span>
                    {concept.relevanceScore > 15 && (
                      <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
                        ⭐ High Match
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-xs text-gray-400 ml-2">
                  #{index + 1}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}
    {showBookmarks && (
      <div className="mt-4">
        <h4 className="font-semibold mb-2">
          ★ Bookmarked Concepts ({bookmarkedConcepts.size})
          {bookmarkedConcepts.size === 0 && (
            <span className="text-sm font-normal text-gray-500 ml-2">- No bookmarks yet</span>
          )}
        </h4>
        {bookmarkedConcepts.size > 0 && (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {(getBookmarkedConcepts() || []).map((concept, index) => (
              <div
                key={concept.key}
                className="p-3 bg-yellow-50 border border-yellow-200 rounded cursor-pointer hover:bg-yellow-100 transition-colors duration-200 hover:shadow-md"
                onClick={() => setSelectedConcept(concept)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedConcept(concept);
                  }
                }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="font-semibold text-yellow-700 mb-1 flex items-center">
                      ★ {concept.title || 'Untitled'}
                    </div>
                    <div className="text-sm text-gray-600 mb-2 line-clamp-2">{concept.explanation || 'No description available'}</div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs px-2 py-1 bg-yellow-200 text-yellow-800 rounded-full">
                        {concept.domain}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleBookmark(concept.key);
                    }}
                    className="ml-2 p-1 text-yellow-600 hover:text-yellow-800 transition-colors"
                    title="Remove bookmark"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    )}
  </div>
);

export default React.memo(SearchBar);